import { Vue, Options } from 'vue-class-component';
import { defineAsyncComponent, PropType } from 'vue';
import {
  PlantDetailDTO,
  AggregateCommandDTO,
  RoomObjectType,
  AggregateCommandType,
  IAggregateCommandDTO,
  IEditAggregateCommandDTO,
  EditAggregateCommandDTO,
} from '@/../client-generator/generated-clients/plants';
import plantsClient from '@/core/services/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { DefaultDevicesSettingsDTO } from 'client-generator/generated-clients/device-settings';
import DeviceSettingsService from '../../../../../core/services/device-settings-services/device-settings-service';
import {
  FETCH_PLANT,
  HANDLE_REFRESH_PLANT,
} from '../../../../../core/utils/emitter-events';
import { NamingService } from '../../../../../core/services/naming.service';

@Options({
  name: 'CommandWizard',
  props: {
    currentPlant: {
      type: Object as PropType<PlantDetailDTO>,
      required: false,
    },
  },
  components: {
    CommandWizardStep1: defineAsyncComponent(
      () => import('./CommandWizardStep1/CommandWizardStep1.vue')
    ),
    CommandWizardStep2: defineAsyncComponent(
      () =>
        import(
          './CommandWizardAssignRoomObjects/CommandWizardAssignRoomObjects.vue'
        )
    ),
    CommandWizardStep3: defineAsyncComponent(
      () => import('./CommandWizardStep3/CommandWizardStep3.vue')
    ),
    CommandWizardStep4: defineAsyncComponent(
      () => import('./CommandWizardStepOverview/CommandWizardStepOverview.vue')
    ),
  },
})
export default class CommandWizard extends Vue {
  plantId = -1;
  isEdit = false;
  activeStep = 0;
  nextDisabled = false;
  aggregateCommandId = '';
  commandGroup: IAggregateCommandDTO = {};
  devicesSettings: DefaultDevicesSettingsDTO[] = [];

  get steps() {
    return [
      {
        title: 'plantDetail.typeNameGroup',
        show: this.commandGroup?.type === AggregateCommandType.Group,
        componentName: 'CommandWizardStep1',
      },
      {
        title: `AppResource.${
          this.commandGroup?.roomObjectType === RoomObjectType.Light
            ? 'AssignTheLights'
            : 'AssignTheShutters'
        }`,
        show: this.commandGroup?.type === AggregateCommandType.Group,
        componentName: 'CommandWizardStep2',
      },
      {
        title: 'AppResource.AssignTheCommands',
        show: true,
        componentName: 'CommandWizardStep3',
      },
      {
        title: 'AppResource.EndTheConfiguration',
        show: true,
        componentName: 'CommandWizardStep4',
      },
    ].filter((s) => s.show);
  }

  get currentStepTitle() {
    return this.$t(this.steps[this.activeStep].title);
  }

  get activeStepComponent() {
    return this.steps[this.activeStep].componentName;
  }

  get buttonSubmitLabel() {
    if (this.activeStep < this.steps.length - 1) {
      return this.$t('AppResource.Next');
    }

    return this.$t('AppResource.SaveAndExit');
  }

  async created() {
    try {
      this.plantId = +this.$route.params.plantId;
      this.aggregateCommandId = this.$route.params.id as string;
      this.isEdit = !!this.aggregateCommandId;
      const aggregateCommandType = Number(
        this.$route.query.aggregateCmdType
      ) as AggregateCommandType;
      const roomObjectType = this.$route.query.roomObjectType
        ? (Number(this.$route.query.roomObjectType) as RoomObjectType)
        : RoomObjectType.None;

      if (this.isEdit) {
        this.$store.dispatch(SET_APP_LOADING, true);
        this.commandGroup = await plantsClient.aggregateCommandsGET(
          this.plantId,
          this.aggregateCommandId
        );
        this.$store.dispatch(SET_APP_LOADING, false);
      } else {
        this.commandGroup = new AggregateCommandDTO({
          devices: [],
          rooms: [],
          type: aggregateCommandType,
          roomObjectType: roomObjectType,
          name:
            aggregateCommandType === AggregateCommandType.General
              ? `${
                  roomObjectType === RoomObjectType.Shutter
                    ? this.$t('AppResource.GroupOfShutters')
                    : this.$t('AppResource.GroupOfLights')
                }`
              : undefined,
        });
      }
    } catch (error) {
      console.log(
        'Error reading command group, redirecting to command groups page.'
      );
      this.goToCommandGroups();
      return;
    }
  }

  handleButtonBackAction() {
    if (this.activeStep > 0) {
      this.nextDisabled = false;
      this.activeStep--;
    } else {
      this.goToCommandGroups();
    }
  }

  async handleButtonSubmitAction() {
    if (this.activeStep < this.steps.length - 1) {
      this.activeStep++;
      return;
    }

    this.$store.dispatch(SET_APP_LOADING, true);
    const dto: IEditAggregateCommandDTO = {
      deviceSettings: DeviceSettingsService.generateDeviceSettingsResultDto(
        this.devicesSettings
      ),
      aggregateCommand: AggregateCommandDTO.fromJS(this.commandGroup),
    };
    try {
      if (this.isEdit) {
        await plantsClient.aggregateCommandsPUT(
          this.plantId,
          this.aggregateCommandId,
          new EditAggregateCommandDTO({
            deviceSettings: dto.deviceSettings,
            aggregateCommand: dto.aggregateCommand,
          })
        );
      } else {
        await plantsClient.aggregateCommandsPOST(
          this.plantId,
          new EditAggregateCommandDTO({
            deviceSettings: dto.deviceSettings,
            aggregateCommand: dto.aggregateCommand,
          })
        );
      }

      this.$emitter.emit(FETCH_PLANT);
      this.goToCommandGroups();
    } catch (error) {
      let messageError = '';
      console.log(error);
      switch (error.status) {
        case 409:
          this.$emitter.emit(HANDLE_REFRESH_PLANT);
          this.$router.push({
            name: 'page-plant-dashboard',
            params: this.$route.params,
          });
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      if (messageError) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 3000,
        });
      }
    } finally {
      NamingService.flushCache();
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  nextEnabledEvent(nextEnabled: boolean) {
    this.nextDisabled = !nextEnabled;
  }

  goToCommandGroups() {
    this.$router.push({ name: 'page-commands-groups' });
  }
}
